import React, { useCallback, useEffect, useState } from "react";
import { RiCloseLine } from "react-icons/ri";
import { FiDownload } from "react-icons/fi";
import Divider from "../../../../../../components/shared/Divider/Divider";
import Button from "../../../../../../components/shared/Button/Button";
import Select from "../../../../../../components/shared/Select/Select";
import { useNewton } from "../../store/NewtonContext";
import { useUserData } from "../../../../../../hooks/useUserData";
import { useExportSession } from "../../hooks/useExportSession";
import { isProd } from "../../../../../../common/envConfig";

export const Header = ({ handleClose }) => {
  const {
    messagesHistory,
    sessionId,
    setSessionId,
    setMessagesHistory,
    adminOverrideUserId,
    setAdminOverrideUserId,
    llmEnv,
    setLlmEnv,
  } = useNewton();
  const { userData } = useUserData();
  const isAdmin = userData?.role === "admin";
  const exportSession = useExportSession();

  const [adminUserInput, setAdminUserInput] = useState("");

  const handleExportCurrentSession = useCallback(() => {
    try {
      // Find display name from sessions if available, fallback to default
      const displayName = `Chat ${sessionId?.slice(-6) || ""}`;

      exportSession({
        sessionId,
        userId: userData?.uid,
        messages: messagesHistory,
        displayName,
      });
    } catch (error) {
      console.error("Failed to export session:", error);
    }
  }, [exportSession, sessionId, userData?.uid, messagesHistory]);

  useEffect(() => {
    setAdminUserInput(adminOverrideUserId || "");
  }, [adminOverrideUserId]);

  const resetSession = () => {
    setSessionId(null);
    setMessagesHistory([]);
  };

  const handleChangeOverride = () => {
    setAdminOverrideUserId(adminUserInput || "");
    resetSession();
  };

  const handleClearOverride = () => {
    setAdminOverrideUserId("");
    setAdminUserInput("");
    resetSession();
  };

  const handleChangeLlmEnv = (value) => {
    setLlmEnv(value);
    resetSession();
  };

  return (
    <div className="bg-grayTrue900">
      <div className="flex items-start justify-between px-4 py-5">
        <div className="flex flex-col gap-1">
          <div className="text-gray200 text-base font-bold">
            Ask SAMI (Beta)
          </div>

          <div className="text-grayTrue400 text-xs">Ask me anything</div>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <div className="mt-2 flex items-center gap-2">
              <input
                className="bg-grayTrue800 text-gray50 w-48 rounded-md px-2 py-1 text-xs outline-none border border-grayTrue700"
                placeholder="Enter user ID to inspect"
                value={adminUserInput}
                onChange={(e) => setAdminUserInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleChangeOverride();
                  }
                }}
              />

              <Button
                color="grayTrue700"
                onClick={
                  adminOverrideUserId
                    ? handleClearOverride
                    : handleChangeOverride
                }
                rounded="md"
                size="xxs"
                type="button"
              >
                {adminOverrideUserId && adminOverrideUserId === adminUserInput
                  ? "Clear"
                  : "Override"}
              </Button>
            </div>
          )}
          {isAdmin && sessionId && (
            <Button
              color="transparent/grayTrue700"
              onClick={handleExportCurrentSession}
              size="xs"
              rounded="md"
              startContent={<FiDownload className="size-3.5" />}
              title="Export current session as JSON"
              type="button"
            >
              Export
            </Button>
          )}
          {isProd && (
            <Select
              btnInitialColor="grayTrue700"
              btnSelectedColor="grayTrue700"
              onChange={handleChangeLlmEnv}
              options={[
                { value: "prod", text: "prod" },
                { value: "staging", text: "staging" },
              ]}
              value={llmEnv}
            />
          )}
          <RiCloseLine
            className="text-grayTrue400 hover:text-gray200 cursor-pointer size-6"
            onClick={handleClose}
          />
        </div>
      </div>
      <Divider color="grayTrue800" type="horizontal" />
    </div>
  );
};
